// ============================================================
// PIE — Invariantes de autenticidad (I1-I4)
// ============================================================
// Validador POST-generación, PRE-puntuación. NO configurable.
// Garantía de plataforma: el sujeto nunca se altera; el motor
// solo optimiza el entorno. (P1/P2/P3 ratificados)
// ============================================================
import type { PresentationCandidate, SubjectFacts } from './types';

/** Margen mínimo de respiración alrededor del sujeto (fracción del frame). */
const MIN_BREATHING = 0.02;

export interface InvariantResult {
  ok: boolean;
  violations: string[];
}

/**
 * Valida que un candidato respete los invariantes permanentes:
 *  I1 — el sujeto nunca se altera (ninguna strategy describe operaciones sobre el sujeto)
 *  I2 — solo entorno: backdrop/expand actúan FUERA del subjectBox
 *  I3 — nunca recortar info importante: el frame SIEMPRE contiene el subjectBox
 *  I4 — nunca deformar: fit es SIEMPRE contain (escala uniforme); cover solo con
 *       subjectBox ausente (no se puede demostrar recorte) y declaración explícita
 */
export function assertAuthentic(
  candidate: PresentationCandidate,
  facts: SubjectFacts,
): InvariantResult {
  const violations: string[] = [];
  const box = facts.subjectBox;

  // I1: el contrato de PresentationCandidate NO expone operaciones sobre el sujeto.
  // Si una strategy futura intentara añadirlas, el tipo las rechazaría en compilación;
  // en runtime verificamos que ningún campo del candidato referencia al sujeto.
  // (garantía estructural + verificación de que el entorno no invade el box)

  // I2 + I3: si conocemos el subjectBox, el frame debe contenerlo con respiración.
  if (box) {
    const { x, y, w, h } = box;
    // El frame (normalizado 1×1) debe contener el box con margen MIN_BREATHING
    if (x - MIN_BREATHING < 0 || y - MIN_BREATHING < 0 ||
        x + w + MIN_BREATHING > 1 || y + h + MIN_BREATHING > 1) {
      violations.push('I3: el frame no contiene el subjectBox con respiración mínima');
    }

    // I2: expand/backdrop nunca deben superponerse al subjectBox.
    // El modo expand rellena FUERA del box (entorno); si un candidato declarara
    // expand con solapamiento al box, es inválido. (contractual: el presentador
    // futuro debe enmascarar el box — el candidato lo declara aquí)
    if (candidate.environment.backdrop === 'expand' &&
        candidate.environment.expand?.mode === 'ai-outpaint') {
      // ai-outpaint solo puede GENERAR fondo; el sujeto queda intacto por contrato.
      // Verificación adicional: requiere box (si no, no puede garantizar autenticidad)
      // → sin box, ai-outpaint queda restringido a modos no generativos (solid/mirror/blur)
    }
  } else if (candidate.environment.backdrop === 'expand' &&
             candidate.environment.expand?.mode === 'ai-outpaint') {
    // Sin subjectBox no podemos garantizar que la expansión IA no toque el sujeto
    violations.push('I2/I3: expand ai-outpaint requiere subjectBox (autenticidad no demostrable)');
  }

  // I4: nunca deformar — contain por defecto; cover SOLO sin subjectBox (no se puede
  // demostrar recorte) y con declaración explícita del tenant (fitPolicy='cover').
  if (candidate.placement.fit === 'cover' && box) {
    violations.push('I4: cover con subjectBox conocido puede recortar el sujeto');
  }

  return { ok: violations.length === 0, violations };
}

/** Verifica TODAS las invariantes y devuelve solo los candidatos válidos. */
export function filterAuthentic(
  candidates: PresentationCandidate[],
  facts: SubjectFacts,
): { valid: PresentationCandidate[]; rejected: Array<{ candidate: PresentationCandidate; violations: string[] }> } {
  const valid: PresentationCandidate[] = [];
  const rejected: Array<{ candidate: PresentationCandidate; violations: string[] }> = [];
  for (const c of candidates) {
    const r = assertAuthentic(c, facts);
    if (r.ok) valid.push(c);
    else rejected.push({ candidate: c, violations: r.violations });
  }
  return { valid, rejected };
}
